import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  FormControl,
  FormLabel,
  Link,
  Typography,
  Box,
  Paper,
  Stack,
  Divider,
  IconButton
} from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getLpaById, updateLpa } from "../../../api/services/document";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { 
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CalendarToday as CalendarIcon
} from "@mui/icons-material";
import { useState } from "react";

const EditLpaModal = ({ show, handleClose, data: selectedId, handleEditSubmit, refecthLpa }) => {
  const { id: clientId } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);

  const { data: lpaData } = useQuery(
    [QK.lpa, selectedId],
    () => getLpaById(selectedId),
    {
      enabled: !!selectedId,
    }
  );

  const editMutation = useMutation(({ id, editData }) => updateLpa(id, editData), {
    onSuccess: () => {
      enqueueSnackbar('LPA updated successfully!', { variant: 'success' });
      handleClose();
      refecthLpa();
    },
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const editData = new FormData();
    if (e.target.lpaHealthFile.files.length > 0) {
      editData.append("cert_health", e.target.lpaHealthFile.files[0]);
    }
  
    if (e.target.lpaFinanceFile.files.length > 0) {
      editData.append("cert_finance", e.target.lpaFinanceFile.files[0]);
    } 
    editData.append("start_date", e.target.startDate.value);
    editData.append("client", clientId);

    if (selectedId) {
      const id = selectedId;
      editMutation.mutate({ id, editData });  
    } 
  };

  return (
    <Dialog 
      open={show} 
      onClose={handleClose} 
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">Edit Lasting Power of Attorney</Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 4 }}>
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{
            '& .MuiFormControl-root': { mb: 3 }
          }}
        >
          <Stack spacing={4}>
            {/* Health Certificate Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Health & Welfare Certificate
              </Typography>
              
              {lpaData?.cert_health && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1
                }}>
                  <FileIcon color="primary" sx={{ mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Link 
                      href={lpaData.cert_health} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 500 }}
                    >
                      View Current Health Certificate
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      Uploaded on {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              )}

              <FormControl fullWidth>
                <Button
                  component="label"
                  variant="outlined"
                  color="primary"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ py: 2, borderRadius: 1 }}
                >
                  Upload New Health Certificate
                  <input
                    type="file"
                    name="lpaHealthFile"
                    hidden
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  PDF, JPG, or PNG (Max 5MB)
                </Typography>
              </FormControl>
            </Paper>

            {/* Finance Certificate Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Property & Financial Affairs Certificate
              </Typography>
              
              {lpaData?.cert_finance && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1
                }}>
                  <FileIcon color="primary" sx={{ mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Link 
                      href={lpaData.cert_finance} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 500 }}
                    >
                      View Current Finance Certificate
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      Uploaded on {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              )}

              <FormControl fullWidth>
                <Button
                  component="label"
                  variant="outlined"
                  color="primary"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ py: 2, borderRadius: 1 }}
                >
                  Upload New Finance Certificate
                  <input
                    type="file"
                    name="lpaFinanceFile"
                    hidden
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  PDF, JPG, or PNG (Max 5MB)
                </Typography>
              </FormControl>
            </Paper>

            {/* Date Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                LPA Details
              </Typography>
              
              <FormControl fullWidth>
                <TextField
                  label="LPA Start Date"
                  type="date"
                  name="startDate"
                  defaultValue={lpaData?.start_date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <CalendarIcon color="action" sx={{ mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      pl: 1.5
                    }
                  }}
                />
              </FormControl>
            </Paper>
          </Stack>

          <Divider sx={{ my: 4 }} />

          <DialogActions sx={{ px: 0 }}>
            <Button 
              onClick={handleClose} 
              variant="outlined"
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 1,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleClose}
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={editMutation.isLoading}
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 1,
                textTransform: 'none',
                boxShadow: 'none'
              }}
            >
              {editMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditLpaModal;